import { Pipe, PipeTransform } from '@angular/core';
import { CoreCurrencyService } from '../core/services/currency.service';

@Pipe({
  name: 'currencyFormatter',
  pure: false,
})
export class CurrencyFormatterPipe implements PipeTransform {
  constructor(private currencyService: CoreCurrencyService) {}

  private rightAlignedCurrencies = ['EUR', 'CHF', 'CZK', 'PLN'];

  transform(
    value: number,
    showPlusSign: boolean = false,
    currencyCode: string = this.currencyService.getSelectedCurrency() || 'USD',
    locale: string = 'en-US',
    twoFractionDigits: boolean = false,
    notation:
      | 'standard'
      | 'scientific'
      | 'engineering'
      | 'compact'
      | undefined = 'standard',
  ): string {
    if (value == null) value = 0;
    if (typeof value == 'string') value = parseFloat(value);
    if (
      !currencyCode ||
      currencyCode === 'undefined' ||
      currencyCode === 'null'
    ) {
      currencyCode = 'USD';
    }

    // Get currency from service if not provided
    let currency = currencyCode.toLocaleUpperCase();

    let fractionDigits = 0;
    if (
      twoFractionDigits ||
      (value < 100 &&
        value > -100 &&
        value !== 0 &&
        value.toFixed(2).split('.')[1] != '00')
    ) {
      fractionDigits = 2;
    }

    const formattedValue = new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
      compactDisplay: 'short',
      notation: notation,
    }).format(value);

    let returnValue = '';

    try {
      // Australian dollar
      if (currency === 'AUD') {
        const formattedValueNumber = Number(formattedValue?.replace(/,/g, ''));

        returnValue =
          formattedValueNumber < 0
            ? `-A$ ${formattedValue?.replace('-', '')}`
            : `A$ ${formattedValue}`;
      } else if (currency === 'CAD') {
        const formattedValueNumber = Number(formattedValue?.replace(/,/g, ''));

        returnValue =
          formattedValueNumber < 0
            ? `-CA$ ${formattedValue?.replace('-', '')}`
            : `CA$ ${formattedValue}`;
      } else if (currency === 'HUF') {
        returnValue = `${formattedValue} Ft`;
      } else if (this.rightAlignedCurrencies.includes(currency)) {
        // Check if the currency should be on the right side
        returnValue = `${formattedValue} ${this.getSymbol(currency)}`;
      } else {
        returnValue = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: fractionDigits,
          ...(notation === 'compact' && { maximumFractionDigits: fractionDigits }),
          useGrouping: true,
          compactDisplay: 'short',
          notation: notation,
        }).format(value);
      }
    } catch (e) {
      // If the currency is not supported, use USD
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: fractionDigits,
        ...(notation === 'compact' && { maximumFractionDigits: fractionDigits }),
        useGrouping: true,
        compactDisplay: 'short',
        notation: notation,
      }).format(value);
    }

    if (showPlusSign && value > 0) {
      returnValue = '+ ' + returnValue;
    }
    if (returnValue?.includes('-')) {
      returnValue = returnValue.replace('-', '- ');
    }

    return returnValue;
  }

  private getSymbol(currencyCode: string): string {
    const currencySymbols: { [key: string]: string } = {
      EUR: '€',
      CHF: 'CHF',
      CZK: 'Kč',
      PLN: 'zł',
    };

    return currencySymbols[currencyCode] || currencyCode;
  }
}
